import { useState } from 'react'

import { initRequestParam } from 'constants/constants'

import _ from 'lodash'
import { useCurrentStatusStore } from 'store/currentStatusStore'
import { getAPI } from 'utils/getAPI'

import type {
  GetMstCalendarCenterSupplierCalendarCenterSupplierGetRequest,
  MasterCalendarCenterSupplierUpdateCalendarCenterSupplierUpdatePostRequest,
  ResponseResult,
  ResponseUpdate,
  CalendarCenterSupplier,
  MstCalendarCenterSupplier,
} from 'api-client'
import type { ErrorResponse } from 'types/types'

export type { GetMstCalendarCenterSupplierCalendarCenterSupplierGetRequest } from 'api-client'

export interface MstCalendarCenterSupplierSeq extends MstCalendarCenterSupplier {
  id: string
}

export interface MstCalendarCenterSuppliersSeq extends CalendarCenterSupplier {
  list: MstCalendarCenterSupplierSeq[]
}

export const useCenterSupplierCalendar = () => {
  const [calendarSearch, setCalendarSearch] = useState<MstCalendarCenterSuppliersSeq>(initRequestParam)
  const [calendarSearchIsLoading, setCalendarSearchIsLoading] = useState(false)
  const [calendarSearchResult, setCalendarSearchResult] = useState<ResponseResult | undefined>(undefined)
  const [calendarUpdateResult, setCalendarUpdateResult] = useState<ResponseUpdate | undefined>(undefined)
  const { setCurrentStatuses } = useCurrentStatusStore()
  const [isLoading, setIsLoading] = useState(false)
  const [isUpdating, setIsUpdating] = useState(false)
  const [result, setResult] = useState<ResponseResult>()

  const fetchCalendarSearch = async (searchParam: GetMstCalendarCenterSupplierCalendarCenterSupplierGetRequest) => {
    setCalendarSearch(initRequestParam)
    setCalendarSearchResult(undefined)
    setCalendarSearchIsLoading(true)
    setResult(undefined)
    setIsLoading(true)

    try {
      const api = await getAPI()
      if (!api) return undefined

      const response = await api.getMstCalendarCenterSupplierCalendarCenterSupplierGet({
        ...searchParam,
        companyId: searchParam.companyId,
        orgCenterId: searchParam.orgCenterId || undefined,
        orgSupplierId: searchParam.orgSupplierId || undefined,
        selectDateRange: searchParam.selectDateRange || undefined,
        sort: searchParam.sort || undefined,
      })

      setCalendarSearch({
        ...response.data,
        list: _.map(response.data.list, (value) => ({
          ...value,
          id: Math.random().toString(32).substring(2),
        }))
      })
      setCalendarSearchResult(response.result)
      setCurrentStatuses(response.result.systemInfo?.sysCurrentStatus)
      setResult(response.result)
      return response
    } catch (e) {
      const error = e as ErrorResponse
      setCalendarSearchResult({
        status: 'error',
        message: error.response && error.response.status === 422 ? '入力値に誤りがあります。' : `センター仕入先カレンダーマスタの取得でエラーが発生しました。`,
        systemDate: null,
        pageNo: 0,
        systemInfo: null,
      })
      return undefined
    } finally {
      setCalendarSearchIsLoading(false)
      setIsLoading(false)
    }
  }

  const fetchCalendarUpdate = async (body: MasterCalendarCenterSupplierUpdateCalendarCenterSupplierUpdatePostRequest): Promise<ResponseUpdate> => {
    setIsUpdating(true);
    setResult(undefined)

    try {
      const api = await getAPI()
      if (!api) throw new Error('API initialization failed')
      
      const response = await api.masterCalendarCenterSupplierUpdateCalendarCenterSupplierUpdatePost(body);

      setCalendarUpdateResult(response);
      setResult(response.result)
      if (response.result.status !== 'success') {
        const error = new Error(response.result.message || '入力エラーがあります');
        Object.assign(error, {
            ...response
        });
        throw error;
      }
      return response;
    } catch (error) {
        if (error && typeof error === 'object' && 'result' in error) {
            const apiResponse = error as ResponseUpdate; 
            await new Promise<void>(resolve => {
              setCalendarUpdateResult(apiResponse);
              resolve();
            });
            return await Promise.reject(apiResponse);
        }
        const { response } = error as any;
        const errorResponse = {
            errors: [],
            result: {
                status: 'error',
                message: response && response.status === 422 ? '入力値に誤りがあります。' : `センター仕入先カレンダーマスタの更新でエラーが発生しました。`,
                systemDate: null,
                pageNo: 0,
                systemInfo: null,
            },
            updateCount: null,
            lastUpdateDatetime: null,
        };
        await new Promise<void>(resolve => {
          setCalendarUpdateResult(errorResponse);
          setResult(errorResponse.result)
          resolve();
        });
        return await Promise.reject(errorResponse);      

    } finally {
      setIsUpdating(false);
    }
  };


  return {
  	result,
  	isLoading,
  	isUpdating,
    calendarSearch,
    fetchCalendarSearch,
    calendarSearchResult,
    calendarSearchIsLoading,

    fetchCalendarUpdate,
    calendarUpdateResult,
  }
}
